import axios from 'axios';
import { store } from './store';
import { getAuthToken, removeAuthToken, setAuthToken, isSessionValid } from './auth/authUtils';
import { API_CONFIG } from './config/api';
import authEvents from './auth/authEvents';

// Create axios instance with advanced configuration
const axiosInstance = axios.create({
    baseURL: API_CONFIG.BASE_URL,
    timeout: API_CONFIG.TIMEOUT.DEFAULT,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json'
    }
});

// Request interceptor for authentication and security
axiosInstance.interceptors.request.use(
    async (config) => {
        try {
            // Add CSRF token
            const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
            if (csrfToken) {
                config.headers['X-CSRF-TOKEN'] = csrfToken;
            }
            
            // Add authentication token
            const authToken = getAuthToken() || store.getState().auth.token;
            if (authToken) {
                config.headers['Authorization'] = `Bearer ${authToken}`;
            }
            
            // Add device fingerprint
            const deviceFingerprint = sessionStorage.getItem('device_fingerprint');
            if (deviceFingerprint) {
                config.headers['X-Device-Fingerprint'] = deviceFingerprint;
            }
            
            // Add language preference
            const lang = localStorage.getItem('i18nextLng') || 'en';
            config.headers['Accept-Language'] = lang;

            // Add request timestamp
            config.headers['X-Request-Time'] = new Date().toISOString();
            
            // Add request ID for tracking
            config.headers['X-Request-ID'] = generateRequestId();
            
            return config;
        } catch (error) {
            console.error('Request interceptor error:', error);
            return config;
        }
    },
    (error) => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
    }
);

// Response interceptor for error handling and token management
axiosInstance.interceptors.response.use(
    (response) => {
        // Log successful requests in development
        if (process.env.NODE_ENV === 'development') {
            console.log(`API Success: ${response.config.method?.toUpperCase()} ${response.config.url}`, {
                status: response.status,
                data: response.data
            });
        }
        return response;
    },
    async (error) => {
        const originalRequest = error.config;
        
        // Log error details
        console.error('API Error:', {
            url: error.config?.url,
            method: error.config?.method,
            status: error.response?.status,
            message: error.message,
            data: error.response?.data
        });
        
        // Handle different error types
        if (error.response) {
            const { status, data } = error.response;
            
            switch (status) {
                case 401:
                    // Skip session-expiry handling for auth endpoints (login, register, etc.)
                    const authEndpoints = ['/login', '/register', '/forgot-password', '/reset-password'];
                    const isAuthEndpoint = authEndpoints.some(ep => originalRequest.url?.includes(ep));

                    if (isAuthEndpoint) {
                        // Let the component handle its own 401 (e.g. wrong credentials)
                        break;
                    }

                    // Unauthorized - try token refresh first, then show session modal
                    if (!originalRequest._retry && !originalRequest._isRefreshRequest) {
                        originalRequest._retry = true;

                        // Try to refresh the token first
                        try {
                            const refreshResponse = await axios.post(
                                `${API_CONFIG.BASE_URL}/refresh-token`,
                                {},
                                {
                                    headers: {
                                        'Authorization': `Bearer ${getAuthToken()}`,
                                    },
                                    withCredentials: true,
                                    _isRefreshRequest: true, // Mark as refresh request
                                }
                            );

                            // Token is in data.data.token (Laravel response structure)
                            const newToken = refreshResponse.data?.data?.token || refreshResponse.data?.token;
                            if (newToken) {
                                // Store new token
                                setAuthToken(newToken);
                                store.dispatch({
                                    type: 'auth/login',
                                    payload: {
                                        user: store.getState().auth.user,
                                        token: newToken,
                                    },
                                });

                                // Retry the original request with new token
                                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                                return axiosInstance(originalRequest);
                            }
                        } catch (refreshError) {
                            console.log('Token refresh failed, showing session expired modal');
                        }

                        // Token refresh failed - show session expired modal instead of redirecting
                        // Don't clear auth immediately - let user re-authenticate
                        authEvents.notifySessionExpired(error);
                    }
                    break;
                    
                case 403:
                    // Forbidden - show error message
                    if (data?.message) {
                        console.error('Access forbidden:', data.message);
                    }
                    break;
                    
                case 429:
                    // Rate limited - show retry information
                    const retryAfter = data?.retry_after || 60;
                    console.warn(`Rate limited. Retry after ${retryAfter} seconds.`);
                    break;
                    
                case 422:
                    // Validation errors - these are handled by components
                    break;
                    
                case 500:
                    // Server error - show generic error
                    console.error('Server error occurred');
                    break;
                    
                default:
                    console.error(`API Error ${status}:`, data?.message || 'Unknown error');
            }
        } else if (error.request) {
            // Network error
            console.error('Network error:', error.message);
            
            // Check if it's a timeout
            if (error.code === 'ECONNABORTED') {
                console.error('Request timeout');
            }
        } else {
            // Other error
            console.error('Request setup error:', error.message);
        }
        
        return Promise.reject(error);
    }
);

// Generate unique request ID
const generateRequestId = () => {
    return 'req_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
};

// Fetch CSRF token with retry logic
const fetchCsrfToken = async (retries = 3) => {
    for (let i = 0; i < retries; i++) {
        try {
            const response = await axios.get(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CSRF_TOKEN}`, {
                withCredentials: true,
                timeout: 5000
            });
            
            if (response.data?.csrf_token) {
                axiosInstance.defaults.headers.common['X-CSRF-TOKEN'] = response.data.csrf_token;
                console.log('CSRF token fetched successfully');
                return;
            }
        } catch (error) {
            console.warn(`CSRF token fetch attempt ${i + 1} failed:`, error.message);
            
            if (i === retries - 1) {
                console.error('Failed to fetch CSRF token after all retries');
            } else {
                // Wait before retry
                await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
            }
        }
    }
};

// Initialize CSRF token
fetchCsrfToken();

// Add retry logic for failed requests
const retryRequest = async (config, retries = 3) => {
    for (let i = 0; i < retries; i++) {
        try {
            return await axiosInstance(config);
        } catch (error) {
            if (i === retries - 1) throw error;
            
            // Wait before retry (exponential backoff)
            await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, i)));
        }
    }
};

// Add retry method to axios instance
axiosInstance.retry = retryRequest;

export default axiosInstance;